import { useState } from 'react';

interface FAQItem {
  question: string;
  answer: string;
}

interface InnerFAQSectionProps {
  faqs: FAQItem[];
}

export default function InnerFAQSection({ faqs }: InnerFAQSectionProps) {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  return (
    <section className="py-28 bg-white relative overflow-hidden" itemScope itemType="https://schema.org/FAQPage">
      <div
        className="absolute bottom-0 left-0 right-0 h-40 pointer-events-none"
        style={{ background: 'linear-gradient(0deg, rgba(247,249,248,0.8) 0%, transparent 100%)' }}
      />

      <div className="max-w-5xl mx-auto px-6 relative z-10">
        <div className="text-center mb-16">
          <div className="flex items-center justify-center gap-3 mb-4">
            <span className="inline-block w-10 h-px bg-gold opacity-70"></span>
            <p className="text-gold text-xs font-bold uppercase tracking-[0.2em]">Common Questions</p>
            <span className="inline-block w-10 h-px bg-gold opacity-70"></span>
          </div>
          <h2 className="font-heading text-navy font-bold mb-3 leading-tight" style={{ fontSize: 'clamp(1.9rem, 3.5vw, 3.2rem)' }}>
            Frequently Asked Questions
          </h2>
          <p className="text-gray-500 text-base max-w-xl mx-auto leading-relaxed">
            Straight answers to the questions families ask most about senior care in Oakland, IL.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3.5">
          {faqs.map((faq, i) => (
            <div
              key={i}
              className={`rounded-2xl border overflow-hidden bg-offWhite transition-all duration-200 ${
                openIndex === i ? 'border-sage/60' : 'border-gray-100 hover:border-gray-200'
              }`}
              itemScope
              itemProp="mainEntity"
              itemType="https://schema.org/Question"
            >
              <button
                onClick={() => setOpenIndex(openIndex === i ? null : i)}
                className="w-full flex items-start justify-between px-6 py-5 text-left cursor-pointer gap-4"
              >
                <h4
                  className="font-heading text-navy font-semibold text-sm leading-snug"
                  itemProp="name"
                >
                  <a href={`#faq-inner-${i}`} className="hover:text-gold transition-colors">
                    {faq.question}
                  </a>
                </h4>
                <div className={`w-8 h-8 flex items-center justify-center rounded-full flex-shrink-0 mt-0.5 transition-all ${
                  openIndex === i ? 'bg-gold text-navy' : 'bg-white text-gray-400 border border-gray-200'
                }`}>
                  <i className={`${openIndex === i ? 'ri-subtract-line' : 'ri-add-line'} text-base`}></i>
                </div>
              </button>

              <div
                id={`faq-inner-${i}`}
                className={`overflow-hidden transition-all duration-300 ${
                  openIndex === i ? 'max-h-72 opacity-100' : 'max-h-0 opacity-0'
                }`}
                itemScope
                itemProp="acceptedAnswer"
                itemType="https://schema.org/Answer"
              >
                <div className="mx-6 mb-5 border-l-2 border-sage/60 pl-4">
                  <p className="text-gray-500 text-sm leading-relaxed" itemProp="text">
                    {faq.answer}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12 text-center">
          <p className="text-gray-400 text-sm mb-4">Have more questions?</p>
          <a
            href="tel:2173462042"
            className="whitespace-nowrap inline-flex items-center gap-2.5 bg-offWhite border border-gray-200 text-navy font-semibold px-6 py-3 rounded-full hover:border-sage hover:bg-sage/8 transition-all text-sm"
          >
            <i className="ri-phone-fill text-gold"></i>
            Call us: (217) 346-2042
          </a>
        </div>
      </div>
    </section>
  );
}
